import fetcher from '../lib/fetcher'
import {
  TNetwork,
  TNetworkWithCounts,
  TCreateNetworkInput,
  TUpdateNetworkInput,
  TUpdateNetworkPreferencesInput,
  TResolvedSection,
} from '../types/network.type'
import {
  TViewportResponse,
  TViewportWeekResponse,
  TViewportWindow,
} from '../types/viewport.type'
import type {TNetworkGeoZones} from '../types/geoZones.type'
import {TDesignables} from '../types/designables.type'
import {DesignablesSchema} from '../schemas/designables.schemas'

// Single-granularity result since T6 dropped the project mode: per-track sections.
type ResolvePathResult = {
  sections: TResolvedSection[]
}

const createNetwork = async (input: TCreateNetworkInput): Promise<TNetwork> => {
  const response = await fetcher.post<TCreateNetworkInput, TNetwork>(
    '/api/network',
    input,
  )
  if (!response.data) {
    throw new Error('Failed to create network')
  }
  return response.data
}

const listNetworksForClient = async (
  clientId: string,
): Promise<TNetworkWithCounts[]> => {
  const response = await fetcher.get<TNetworkWithCounts[]>(
    `/api/network/client/${clientId}`,
  )
  return response.data ?? []
}

const updateNetwork = async (
  id: string,
  input: TUpdateNetworkInput,
): Promise<TNetwork> => {
  const response = await fetcher.patch<TUpdateNetworkInput, TNetwork>(
    `/api/network/${id}`,
    input,
  )
  if (!response.data) {
    throw new Error('Failed to update network')
  }
  return response.data
}

const updateNetworkPreferences = async (
  networkId: string,
  input: TUpdateNetworkPreferencesInput,
): Promise<TNetwork> => {
  const response = await fetcher.patch<
    TUpdateNetworkPreferencesInput,
    TNetwork
  >(`/api/network/${networkId}/preferences`, input)
  if (!response.data) {
    throw new Error('Failed to update network preferences')
  }
  return response.data
}

const softDeleteNetwork = async (id: string): Promise<void> => {
  await fetcher.del(`/api/network/${id}`)
}

const getNetworkGeoZones = async (
  networkId: string,
): Promise<TNetworkGeoZones> => {
  const response = await fetcher.get<TNetworkGeoZones>(
    `/api/network/${networkId}/geo-zones`,
  )
  if (!response.data) throw new Error('Failed to get network geo zones')
  return response.data
}

const getNetworkDesignables = async (
  networkId: string,
): Promise<TDesignables> => {
  const response = await fetcher.get<TDesignables>(
    `/api/network/${networkId}/designables`,
  )
  return DesignablesSchema.parse(response.data)
}

const resolvePath = async (
  networkId: string,
  pkStart: number,
  pkEnd: number,
  granularity: 'track' | 'km' = 'track',
): Promise<ResolvePathResult> => {
  const params = new URLSearchParams()
  params.set('pkStart', String(pkStart))
  params.set('pkEnd', String(pkEnd))
  params.set('granularity', granularity)
  const response = await fetcher.get<ResolvePathResult>(
    `/api/network/${networkId}/resolve-path?${params.toString()}`,
  )
  if (!response.data) throw new Error('Failed to resolve path')
  return response.data
}

/** Applies the discriminated window (sectorIds OR pk bounds) to `params`. */
const applyViewportWindow = (
  params: URLSearchParams,
  window: TViewportWindow,
): void => {
  if ('sectorIds' in window) {
    window.sectorIds.forEach(id => params.append('sectorIds', id))
  } else {
    params.set('pkStart', String(window.pkStart))
    params.set('pkEnd', String(window.pkEnd))
  }
}

const getViewport = async (
  networkId: string,
  window: TViewportWindow,
  trackIds: string[] = [],
): Promise<TViewportResponse> => {
  const params = new URLSearchParams()
  applyViewportWindow(params, window)
  trackIds.forEach(id => params.append('trackIds', id))
  const response = await fetcher.get<TViewportResponse>(
    `/api/network/${networkId}/viewport?${params.toString()}`,
  )
  if (!response.data) throw new Error('Failed to load the geographic viewport')
  return response.data
}

const getViewportWeek = async (
  networkId: string,
  window: TViewportWindow,
  weekNumber: string,
  year: string,
  trackIds: string[] = [],
): Promise<TViewportWeekResponse> => {
  const params = new URLSearchParams()
  applyViewportWindow(params, window)
  params.set('weekNumber', weekNumber)
  params.set('year', year)
  trackIds.forEach(id => params.append('trackIds', id))
  const response = await fetcher.get<TViewportWeekResponse>(
    `/api/network/${networkId}/viewport/week?${params.toString()}`,
  )
  if (!response.data) throw new Error('Failed to load the viewport week')
  return response.data
}

const NetworkService = {
  createNetwork,
  listNetworksForClient,
  updateNetwork,
  updateNetworkPreferences,
  softDeleteNetwork,
  getNetworkGeoZones,
  getNetworkDesignables,
  resolvePath,
  getViewport,
  getViewportWeek,
}

export default NetworkService
